<template>
  <div class="fr-wrapper">
    <div
      v-if="!orderedFields.length"
      class="text-center text-muted py-3"
    >
      Nenhum campo configurado para este formulário.
    </div>

    <!-- fieldset desabilita de uma vez todo campo e botão de dentro (ocorrência finalizada: só leitura) -->
    <fieldset
      v-else
      :disabled="readonly"
      class="fr-fieldset"
    >
      <div
        v-for="field in visibleFields"
        :key="field.id"
        class="form-group fr-field"
      >
        <label
          v-if="field.field_type !== 'section_break'"
          :for="`fr-${field.id}`"
        >
          {{ field.label }}
          <span
            v-if="field.required"
            class="text-danger"
          >*</span>
        </label>
        <small
          v-if="field.field_type !== 'section_break' && field.help_text"
          class="form-text text-muted mt-0 mb-1"
        >{{ field.help_text }}</small>

        <!-- text -->
        <b-form-input
          v-if="field.field_type === 'text'"
          :id="`fr-${field.id}`"
          v-model="answers[field.id].value"
          :state="fieldState(field)"
        />

        <!-- textarea -->
        <b-form-textarea
          v-else-if="field.field_type === 'textarea'"
          :id="`fr-${field.id}`"
          v-model="answers[field.id].value"
          rows="3"
          :state="fieldState(field)"
        />

        <!-- number -->
        <b-form-input
          v-else-if="field.field_type === 'number'"
          :id="`fr-${field.id}`"
          v-model.number="answers[field.id].value"
          type="number"
          step="any"
          class="fr-number-input"
          :state="fieldState(field)"
        />

        <!-- date -->
        <input
          v-else-if="field.field_type === 'date'"
          :id="`fr-${field.id}`"
          v-model="answers[field.id].value"
          type="date"
          class="form-control fr-date-input"
          :class="{ 'is-invalid': fieldState(field) === false }"
        >

        <!-- boolean -->
        <div
          v-else-if="field.field_type === 'boolean'"
        >
          <b-form-radio-group
            :id="`fr-${field.id}`"
            v-model="answers[field.id].value"
            :options="boolOptions"
            buttons
            button-variant="outline-primary"
            size="sm"
          />
        </div>

        <!-- single_choice -->
        <template v-else-if="field.field_type === 'single_choice'">
          <b-form-radio-group
            :id="`fr-${field.id}`"
            v-model="answers[field.id].optionId"
            stacked
          >
            <b-form-radio
              v-for="opt in field.options"
              :key="opt.id"
              :value="opt.id"
            >
              {{ opt.label }}
            </b-form-radio>
          </b-form-radio-group>
          <p
            v-if="!field.options || !field.options.length"
            class="text-muted small mb-0"
          >
            Nenhuma opção cadastrada.
          </p>
        </template>

        <!-- multiple_choice -->
        <template v-else-if="field.field_type === 'multiple_choice'">
          <b-form-checkbox
            v-for="opt in field.options"
            :key="opt.id"
            v-model="answers[field.id].optionIds"
            :value="opt.id"
          >
            {{ opt.label }}
          </b-form-checkbox>
          <p
            v-if="!field.options || !field.options.length"
            class="text-muted small mb-0"
          >
            Nenhuma opção cadastrada.
          </p>
        </template>

        <!-- photo -->
        <div
          v-else-if="field.field_type === 'photo'"
          class="fr-file-row"
        >
          <img
            v-if="answers[field.id].previewUrl"
            :src="answers[field.id].previewUrl"
            class="fr-photo-preview"
          >
          <img
            v-else-if="answers[field.id].existingFileUrl"
            :src="answers[field.id].existingFileUrl"
            class="fr-photo-preview"
          >
          <input
            :ref="`photoInput${field.id}`"
            type="file"
            accept="image/*"
            style="display: none"
            @change="onFileSelected($event, field)"
          >
          <button
            type="button"
            class="btn btn-sm btn-outline-primary"
            @click="$refs[`photoInput${field.id}`][0].click()"
          >
            <feather-icon
              icon="CameraIcon"
              size="13"
              class="mr-25"
            />
            {{ (answers[field.id].previewUrl || answers[field.id].existingFileUrl) ? 'Substituir foto' : 'Selecionar foto' }}
          </button>
        </div>

        <!-- file -->
        <div
          v-else-if="field.field_type === 'file'"
          class="fr-file-row"
        >
          <a
            v-if="!answers[field.id].file && answers[field.id].existingFileUrl"
            :href="answers[field.id].existingFileUrl"
            target="_blank"
            class="fr-file-link"
          >
            <feather-icon
              icon="PaperclipIcon"
              size="12"
              class="mr-25"
            />
            {{ answers[field.id].existingFileName || 'Arquivo anexado' }}
          </a>
          <span
            v-if="answers[field.id].file"
            class="fr-file-picked"
          >
            <feather-icon
              icon="FileIcon"
              size="12"
              class="mr-25"
            />
            {{ answers[field.id].file.name }}
          </span>
          <input
            :ref="`fileInput${field.id}`"
            type="file"
            style="display: none"
            @change="onFileSelected($event, field)"
          >
          <button
            type="button"
            class="btn btn-sm btn-outline-primary"
            @click="$refs[`fileInput${field.id}`][0].click()"
          >
            <feather-icon
              icon="PaperclipIcon"
              size="13"
              class="mr-25"
            />
            {{ (answers[field.id].file || answers[field.id].existingFileUrl) ? 'Substituir arquivo' : 'Selecionar arquivo' }}
          </button>
        </div>

        <!-- image_annotation -->
        <div
          v-else-if="field.field_type === 'image_annotation'"
          class="fr-annot"
        >
          <div class="fr-annot__imgwrap">
            <img
              :src="field.base_image_url"
              class="fr-annot__img"
              @click="onImageClick($event, field)"
            >
            <div
              v-for="(m, idx) in answers[field.id].markings"
              :key="idx"
              class="fr-annot__dot"
              :style="{ left: `calc(${m.x * 100}% - 6px)`, top: `calc(${m.y * 100}% - 6px)` }"
              :title="m.note"
              @click.stop="removeMark(field, idx)"
            />
          </div>
          <div
            v-if="pendingMark && pendingMark.fieldId === field.id"
            class="fr-annot__form mt-2"
          >
            <v-select
              v-model="pendingOptionId"
              :options="field.options || []"
              :reduce="o => o.id"
              label="label"
              class="vselect mb-1"
              placeholder="Tipo da marcação (opcional)..."
            />
            <textarea
              v-model="pendingNote"
              class="form-control mb-1"
              rows="2"
              placeholder="Observação..."
            />
            <div class="d-flex justify-content-end fr-annot__actions">
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                @click="cancelMark"
              >
                Cancelar
              </button>
              <button
                type="button"
                class="btn btn-sm btn-primary"
                @click="confirmMark"
              >
                Adicionar marcação
              </button>
            </div>
          </div>
          <p
            v-if="!answers[field.id].markings.length && !pendingMark"
            class="text-muted small mt-1 mb-0"
          >
            Clique na imagem para adicionar uma marcação.
          </p>
        </div>

        <!-- section_break -->
        <template v-else-if="field.field_type === 'section_break'">
          <hr>
          <h6>{{ field.label }}</h6>
          <p
            v-if="field.help_text"
            class="text-muted small"
          >
            {{ field.help_text }}
          </p>
        </template>

        <div
          v-if="showValidation && field.required && !isAnswered(field)"
          class="invalid-feedback d-block"
        >
          Este campo é obrigatório.
        </div>
      </div>

      <div
        v-if="!readonly"
        class="d-flex justify-content-end fr-footer"
      >
        <b-button
          variant="primary"
          size="sm"
          :disabled="saving"
          @click="save"
        >
          <b-spinner
            v-if="saving"
            small
            class="mr-25"
          />
          <feather-icon
            v-else
            icon="SaveIcon"
            size="13"
            class="mr-25"
          />
          Salvar
        </b-button>
      </div>
    </fieldset>
  </div>
</template>

<script>
import {
  BFormInput, BFormTextarea, BFormRadioGroup, BFormRadio, BFormCheckbox, BButton, BSpinner,
} from 'bootstrap-vue'

export default {
  name: 'FormRenderer',
  components: {
    BFormInput, BFormTextarea, BFormRadioGroup, BFormRadio, BFormCheckbox, BButton, BSpinner,
  },
  props: {
    form: { type: Object, required: true },
    occurrenceInvolvedId: { type: [Number, String], required: true },
    readonly: { type: Boolean, default: false },
  },
  data() {
    return {
      answers: {},
      saving: false,
      showValidation: false,
      boolOptions: [{ text: 'Sim', value: true }, { text: 'Não', value: false }],
      pendingMark: null,
      pendingNote: '',
      pendingOptionId: null,
    }
  },
  computed: {
    orderedFields() {
      return [...(this.form.fields || [])].sort((a, b) => (a.order || 0) - (b.order || 0))
    },
    fieldsById() {
      const map = {};
      (this.form.fields || []).forEach(f => { map[f.id] = f })
      return map
    },
    rootFieldId() {
      const root = (this.form.fields || []).find(f => f.is_root)
      if (root) return root.id
      return this.orderedFields.length ? this.orderedFields[0].id : null
    },
    // Campos visíveis: caminha a partir da raiz seguindo a ramificação já
    // respondida; para no primeiro campo de ramificação (boolean/single_choice)
    // ainda sem resposta — é isso que "revela" o próximo bloco progressivamente,
    // sem esconder um campo por vez como um wizard tradicional.
    visibleFields() {
      const list = []
      const seen = new Set()
      let currentId = this.rootFieldId
      while (currentId && this.fieldsById[currentId] && !seen.has(currentId)) {
        seen.add(currentId)
        const field = this.fieldsById[currentId]
        list.push(field)
        const a = this.answers[field.id]
        if (field.field_type === 'boolean') {
          if (!a || a.value === null || a.value === undefined) break
          // Sem NENHUM "próximo campo" configurado (nem sim nem não), segue a
          // ordem normal em vez de simplesmente terminar o formulário aqui —
          // ramificação só é um "desvio" quando o admin realmente configurou um.
          const hasBranch = !!(field.next_field_id_if_yes || field.next_field_id_if_no)
          const target = a.value ? field.next_field_id_if_yes : field.next_field_id_if_no
          currentId = hasBranch ? (target || null) : this.nextByOrder(field)
        } else if (field.field_type === 'single_choice') {
          if (!a || !a.optionId) break
          const opt = (field.options || []).find(o => o.id === a.optionId)
          currentId = (opt && opt.next_field_id) || this.nextByOrder(field)
        } else {
          currentId = this.nextByOrder(field)
        }
      }
      return list
    },
  },
  created() {
    (this.form.fields || []).forEach(f => this.$set(this.answers, f.id, this.blankEntry(f)))
    this.initFromSubmission()
  },
  methods: {
    blankEntry(field) {
      switch (field.field_type) {
        case 'multiple_choice':
          return { optionIds: [] }
        case 'single_choice':
          return { optionId: null }
        case 'boolean':
          return { value: null }
        case 'photo':
        case 'file':
          return {
            file: null, previewUrl: null, existingFileUrl: null, existingFileName: null,
          }
        case 'image_annotation':
          return { markings: [] }
        case 'section_break':
          return {}
        default:
          return { value: '' }
      }
    },
    nextByOrder(field) {
      const idx = this.orderedFields.findIndex(f => f.id === field.id)
      if (idx === -1 || idx === this.orderedFields.length - 1) return null
      return this.orderedFields[idx + 1].id
    },
    // Pré-carrega respostas de uma submissão já existente (edição).
    initFromSubmission() {
      const { submission } = this.form
      if (!submission || !submission.answers) return
      submission.answers.forEach(ans => {
        const field = this.fieldsById[ans.form_field_id]
        if (!field) return
        let entry
        switch (field.field_type) {
          case 'text':
          case 'textarea':
            entry = { value: ans.answer_text }
            break
          case 'number':
            entry = { value: ans.answer_number }
            break
          case 'date':
            entry = { value: ans.answer_date }
            break
          case 'boolean':
            entry = { value: ans.answer_boolean }
            break
          case 'single_choice':
            entry = { optionId: ans.form_field_option_id }
            break
          case 'multiple_choice':
            entry = { optionIds: ans.selected_option_ids || [] }
            break
          case 'photo':
          case 'file':
            entry = {
              file: null,
              previewUrl: null,
              existingFileUrl: ans.file_url || null,
              existingFileName: ans.file_name || null,
            }
            break
          case 'image_annotation':
            entry = {
              markings: (ans.annotations || []).map(a => ({
                x: a.x, y: a.y, note: a.note, optionId: a.form_field_option_id,
              })),
            }
            break
          default:
            entry = {}
        }
        this.$set(this.answers, field.id, entry)
      })
    },
    isAnswered(field) {
      const a = this.answers[field.id]
      if (!a) return false
      switch (field.field_type) {
        case 'text':
        case 'textarea':
          return !!(a.value && String(a.value).trim())
        case 'number':
          return a.value !== null && a.value !== undefined && a.value !== ''
        case 'date':
          return !!a.value
        case 'boolean':
          return a.value !== null && a.value !== undefined
        case 'single_choice':
          return !!a.optionId
        case 'multiple_choice':
          return (a.optionIds || []).length > 0
        case 'photo':
        case 'file':
          return !!(a.file || a.existingFileUrl)
        case 'image_annotation':
          return (a.markings || []).length > 0
        case 'section_break':
          return true
        default:
          return false
      }
    },
    fieldState(field) {
      if (!this.showValidation || !field.required) return null
      return this.isAnswered(field) ? null : false
    },

    // ── Foto / Arquivo ───────────────────────────────────────────────
    onFileSelected(e, field) {
      const file = e.target.files && e.target.files[0]
      if (!file) return
      const entry = this.answers[field.id]
      entry.file = file
      if (field.field_type === 'photo') {
        const reader = new FileReader()
        reader.onload = ev => { entry.previewUrl = ev.target.result }
        reader.readAsDataURL(file)
      }
      e.target.value = ''
    },

    // ── Marcação em imagem ───────────────────────────────────────────
    onImageClick(e, field) {
      const { target } = e
      const x = e.offsetX / target.clientWidth
      const y = e.offsetY / target.clientHeight
      this.pendingMark = { fieldId: field.id, x, y }
      this.pendingNote = ''
      this.pendingOptionId = null
    },
    confirmMark() {
      if (!this.pendingMark) return
      const entry = this.answers[this.pendingMark.fieldId]
      entry.markings.push({
        x: this.pendingMark.x, y: this.pendingMark.y, note: this.pendingNote, optionId: this.pendingOptionId,
      })
      this.pendingMark = null
      this.pendingNote = ''
      this.pendingOptionId = null
    },
    cancelMark() {
      this.pendingMark = null
      this.pendingNote = ''
      this.pendingOptionId = null
    },
    removeMark(field, idx) {
      this.answers[field.id].markings.splice(idx, 1)
    },

    // ── Montagem do payload e envio ─────────────────────────────────
    buildAnsweredArray() {
      return this.visibleFields
        .filter(field => field.field_type !== 'section_break')
        .map(field => {
          const a = this.answers[field.id]
          const entry = { form_field_id: field.id }
          switch (field.field_type) {
            case 'text':
            case 'textarea':
              entry.answer_text = a.value !== undefined ? a.value : null
              break
            case 'number':
              entry.answer_number = a.value !== undefined ? a.value : null
              break
            case 'date':
              entry.answer_date = a.value !== undefined ? a.value : null
              break
            case 'boolean':
              entry.answer_boolean = a.value !== undefined ? a.value : null
              break
            case 'single_choice':
              entry.form_field_option_id = a.optionId !== undefined ? a.optionId : null
              break
            case 'multiple_choice':
              entry.selected_option_ids = a.optionIds || []
              break
            case 'photo':
            case 'file':
              if (a.file) entry.file_field_name = `file_${field.id}`
              break
            case 'image_annotation':
              entry.annotations = (a.markings || []).map(m => ({
                x: m.x, y: m.y, note: m.note, form_field_option_id: m.optionId,
              }))
              break
            default:
              break
          }
          return entry
        })
    },
    async save() {
      this.showValidation = true
      const missing = this.visibleFields.some(f => f.required && !this.isAnswered(f))
      if (missing) {
        this.$message('Campos obrigatórios', 'Preencha os campos obrigatórios antes de salvar.', 'error')
        return
      }
      if (this.saving) return
      this.saving = true
      try {
        const answeredArray = this.buildAnsweredArray()
        const fd = new FormData()
        fd.append('form_definition_id', this.form.id)
        fd.append('occurrence_involved_id', this.occurrenceInvolvedId)
        fd.append('answers_json', JSON.stringify(answeredArray))
        answeredArray.forEach(entry => {
          if (entry.file_field_name) {
            const a = this.answers[entry.form_field_id]
            if (a && a.file) fd.append(entry.file_field_name, a.file)
          }
        })
        const res = await this.$http.post(`${this.$store.state.api}form_submissions`, fd)
        this.$message('Formulário salvo', 'Respostas salvas com sucesso.', 'success')
        this.$emit('saved', res.data)
      } catch (e) {
        const msg = e.response && e.response.data && e.response.data.message
          ? e.response.data.message
          : 'Erro ao salvar formulário.'
        this.$message('Erro', msg, 'error')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.fr-fieldset { min-width: 0; margin: 0; padding: 0; border: 0; }

.fr-wrapper {
  padding: 2px;
}
.fr-field {
  margin-bottom: 1.1rem;
}
.fr-number-input,
.fr-date-input {
  max-width: 220px;
}

.fr-file-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.fr-photo-preview {
  display: block;
  max-width: 120px;
  max-height: 120px;
  border-radius: 6px;
  object-fit: cover;
  border: 1px solid #ebe9f1;
}
.fr-file-link,
.fr-file-picked {
  display: inline-flex;
  align-items: center;
  font-size: 12.5px;
  color: #5e5873;
}

.fr-annot__imgwrap {
  position: relative;
  display: inline-block;
  max-width: 100%;
}
.fr-annot__img {
  cursor: crosshair;
  max-width: 100%;
  max-height: 320px;
  display: block;
  border-radius: 6px;
  border: 1px solid #ebe9f1;
}
.fr-annot__dot {
  position: absolute;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #ea5455;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px #ea5455;
  cursor: pointer;
}
.fr-annot__form {
  max-width: 360px;
  padding: 8px;
  border: 1px solid #ebe9f1;
  border-radius: 6px;
}
.fr-annot__actions { gap: 6px; }

.fr-footer {
  padding-top: 0.75rem;
  margin-top: 0.25rem;
  border-top: 1px solid #f3f2f7;
}

.dark-layout .fr-footer { border-top-color: #3b4253; }
.dark-layout .fr-photo-preview,
.dark-layout .fr-annot__img { border-color: #3b4253; }
.dark-layout .fr-file-link,
.dark-layout .fr-file-picked { color: #d0d2d6; }
.dark-layout .fr-annot__form { border-color: #3b4253; }
</style>
